import * as React from 'react';
import { FC, useState, ReactNode } from 'react';
import { pickBy, isEmpty } from 'lodash';
import OtpAttempt from 'app/OtpAttempt';
import type { Locales } from 'app/helpers/createLocalesContext';
import { translation } from 'app/helpers/createLocalesContext';

export type TwofaActivationFormProps = {
  url: string;
  authenticity_token: string;
  all_scopes: string[];

  otp_secret: string;
  scopes: string[];

  locales: Locales;
  error: string | null;
};

const useForm = (
  props: TwofaActivationFormProps
): [boolean, string, (v: string) => void, string[], (v: string) => void] => {
  const [otpAttempt, setOtpAttempt] = useState<string>('');
  const [scopes, setScopes] = useState<string[]>(props.scopes);

  const isValid = isEmpty(
    pickBy(
      {
        otp_attempt: /\d{6}/.test(otpAttempt),
        scopes: scopes.length,
      },
      (e) => !e
    )
  );

  const changeScopes = (scope: string) => {
    setScopes((ss) => (ss.includes(scope) ? ss.filter((s) => s !== scope) : ss.concat(scope)));
  };

  return [isValid, otpAttempt, setOtpAttempt, scopes, changeScopes];
};

const Scope: FC<{ label: string; checked: boolean; scope: string; changeScopes: (v: string) => void }> = ({
  label,
  checked,
  scope,
  changeScopes,
}) => (
  <label className="checkbox">
    <input type="checkbox" checked={checked} onChange={() => changeScopes(scope)} value={scope} name="scopes[]" />
    <span>{label}</span>
  </label>
);

const Scopes: FC<{
  locales: TwofaActivationFormProps['locales'];
  all_scopes: TwofaActivationFormProps['all_scopes'];
  scopes: string[];
  changeScopes: (v: string) => void;
}> = ({ locales, all_scopes, scopes, changeScopes }) => (
  <div className="scopes">
    <div>
      {all_scopes.map((scope) => (
        <Scope
          label={translation(locales, scope)}
          scope={scope}
          checked={scopes.includes(scope)}
          changeScopes={changeScopes}
          key={scope}
        />
      ))}
    </div>
  </div>
);

const Attempt: FC<{
  otpAttempt: string;
  setOtpAttempt: (v: string) => void;
  error: TwofaActivationFormProps['error'];
}> = ({ otpAttempt, setOtpAttempt, error }) => (
  <>
    <p>
      <OtpAttempt otpAttempt={otpAttempt} setOtpAttempt={setOtpAttempt} error={!!error} />
    </p>
    {error && <p className="error_text">{error}</p>}
  </>
);

const Submit: FC<{ label: string; isValid: boolean }> = ({ label, isValid }) => (
  <p>
    <input
      type="submit"
      value={label}
      disabled={!isValid}
      className={`rs-button_small rs-button_light-red rs-button rs-radius ${isValid ? '' : 'disabled'}`}
    />
  </p>
);

const Block: FC<{ title: string; text: string; children?: ReactNode }> = ({ title, text, children }) => (
  <div className="rs-row">
    <div className="rs-column-medium-10 rs-column-medium-offset-1 rs-panel rs-bg_white rs-radius rs-text-align_center">
      <h3>{title}</h3>
      <p>{text}</p>
      {children}
    </div>
  </div>
);

const TwofaActivationForm: FC<TwofaActivationFormProps> = (props) => {
  const [isValid, otpAttempt, setOtpAttempt, scopes, changeScopes] = useForm(props);
  const l = (key: string) => translation(props.locales, key);

  return (
    <form action={props.url} method="post" acceptCharset="UTF-8" className="simple_form twofa-verification">
      <input type="hidden" name="authenticity_token" value={props.authenticity_token} />
      <input type="hidden" name="otp_secret" id="otp_secret" value={props.otp_secret} />

      <Block title={l('step2_title')} text={l('step2_text')}>
        <Scopes locales={props.locales} all_scopes={props.all_scopes} scopes={scopes} changeScopes={changeScopes} />
      </Block>

      <Block title={l('step3_title')} text={l('step3_text')} />

      <Block title={l('step4_title')} text={l('step4_text')}>
        <Attempt otpAttempt={otpAttempt} setOtpAttempt={setOtpAttempt} error={props.error} />
        <Submit label={l('submit')} isValid={isValid} />
      </Block>
    </form>
  );
};

const WrappedTwofaActivationForm: FC<TwofaActivationFormProps> = (props) => <TwofaActivationForm {...props} />;

export default WrappedTwofaActivationForm;
